import React, { useState } from "react";
import { Box } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useTheme as useCustomTheme } from "../theme/useTheme";
import { brand } from "../theme/theme";
import { Button } from "./Button";

export const Tabs = ({
  tabs,
  defaultValue,
  value,
  onValueChange,
  children,
  showNavigation = false,
  saveButton = null,
  lastTabButton = null,
  contentSx,
}) => {
  const theme = useTheme();
  const { neutrals } = useCustomTheme();
  const [internalTab, setInternalTab] = useState(defaultValue || tabs[0]?.value);
  // Con `value` el padre controla la pestaña activa (p. ej. para validar antes de avanzar).
  const isControlled = value !== undefined;
  const activeTab = isControlled ? value : internalTab;

  const handleTabChange = (nextValue) => {
    if (!isControlled) setInternalTab(nextValue);
    onValueChange?.(nextValue);
  };

  const currentTabIndex = tabs.findIndex((tab) => tab.value === activeTab);
  const canGoPrevious = currentTabIndex > 0;
  const canGoNext = currentTabIndex < tabs.length - 1;
  const isLastTab = currentTabIndex === tabs.length - 1;

  const goToPrevious = () => {
    if (canGoPrevious) {
      const previousTab = tabs[currentTabIndex - 1];
      handleTabChange(previousTab.value);
    }
  };

  const goToNext = () => {
    if (canGoNext) {
      const nextTab = tabs[currentTabIndex + 1];
      handleTabChange(nextTab.value);
    }
  };

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: 0,
      }}
    >
      {/* Tab List - iOS Style - Fijo en la parte superior */}
      <Box
        sx={{
          display: "flex",
          backgroundColor: neutrals.gray[100],
          borderRadius: "12px",
          padding: "4px",
          marginBottom: 3,
          position: "relative",
          flexShrink: 0, // No se comprime
          // En pantallas estrechas las pestañas no se aplastan: la barra
          // hace scroll horizontal dentro de su propio contenedor.
          overflowX: "auto",
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        {tabs.map((tab, index) => (
          <button
            key={tab.value}
            onClick={() => handleTabChange(tab.value)}
            style={{
              position: "relative",
              flex: 1,
              minWidth: "max-content",
              whiteSpace: "nowrap",
              background: "none",
              border: "none",
              padding: "10px 16px",
              cursor: "pointer",
              fontSize: "1rem",
              fontWeight: 500,
              color:
                activeTab === tab.value
                  ? neutrals.gray[900]
                  : neutrals.gray[600],
              borderRadius: "8px",
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              outline: "none",
              fontFamily: theme.typography.fontFamily,
              zIndex: 2,
              ...(activeTab === tab.value && {
                backgroundColor: neutrals.white,
                boxShadow:
                  "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)",
                color: neutrals.gray[900],
              }),
            }}

          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
              }}
            >
              {/* Número circular */}
              <Box
                sx={{
                  width: "20px",
                  height: "20px",
                  borderRadius: "50%",
                  backgroundColor: brand.inkFill,
                  color: brand.inkFillText,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "0.75rem",
                  fontWeight: 600,
                  flexShrink: 0,
                }}
              >
                {index + 1}
              </Box>
              {tab.label}
            </Box>
          </button>
        ))}
      </Box>

      {/* Tab Content - usa el scroll del contenedor padre */}
      <Box sx={{ flex: 1, mb: 3, minHeight: 0, overflow: "hidden", display: "flex", flexDirection: "column", ...contentSx }}>
        {React.Children.map(children, (child) => {
          const isActive = child?.props?.value === activeTab;
          return (
            <Box
              key={child?.props?.value}
              sx={{
                flex: 1,
                display: isActive ? "flex" : "none",
                flexDirection: "column",
                minHeight: 0,
                ...(isActive && {
                  animation: "fadeIn 0.2s ease-in-out",
                  "@keyframes fadeIn": {
                    "0%": {
                      opacity: 0,
                      transform: "translateY(4px)",
                    },
                    "100%": {
                      opacity: 1,
                      transform: "translateY(0)",
                    },
                  },
                }),
              }}
            >
              {child}
            </Box>
          );
        })}
      </Box>

      {/* Navigation Buttons */}
      {showNavigation && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 2,
            flexShrink: 0, // No se comprime
            pb: "24px", // Padding bottom
          }}
        >
          <Box sx={{ flex: 1 }}>
            {canGoPrevious ? (
              <Button
                variant="outline"
                onClick={goToPrevious}
                sx={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <ChevronLeft size={16} />
                Anterior
              </Button>
            ) : (
              saveButton && <Box sx={{ width: "100%" }}>{saveButton}</Box>
            )}
          </Box>

          <Box sx={{ flex: 1 }}>
            {canGoNext ? (
              <Button
                variant="secondary"
                onClick={goToNext}
                sx={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                Siguiente
                <ChevronRight size={16} />
              </Button>
            ) : (
              isLastTab && lastTabButton && <Box sx={{ width: "100%" }}>{lastTabButton}</Box>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export const TabsContent = ({ value, children }) => {
  return (
    <Box sx={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
      {children}
    </Box>
  );
};
